<template>
    <div class="kool-choiz" :class="[{disabled}, $attrs.class ?? {}]" ref="container" :tabindex="tabindex" @click.prevent="open" @keydown.space="handleSpace"
        @keyup.enter.prevent="open"
        @keyup.space="open"
        role="combobox" aria-haspopup="true" :aria-expanded="getPopoverVisible() ? 'true' : 'false'" :aria-labelledby="labelId" :aria-controls="labelId" :aria-owns="listId"
        :aria-activedescendant="`select_${_uid}_option_${focusOption}`" :aria-multiselectable="multiple ? 'true' : 'false'">
        <span v-if="selectLabel" class="margin-left-2">{{selectLabel}}</span>
        <select ref="select" class="kool-choiz-select" :id="_id" :name="name" :multiple="multiple" :class="[size]" :required="_required"
            v-bind="{...$attrs, ...ariaValidationAttributes}"
            v-model="selectedModel" @click="open" :disabled="disabled" @focus="focusContainer" tabindex="-1" aria-hidden="true" aria-label="Auswahl">
            <option v-for="option in parsedOptions" :value="option.value" v-text="option.label"
                :disabled="option.disabled || isDynamicDeactivated(option)"></option>
        </select>

        <app-popover ref="popover" class="kool-choiz-wrapper" position="bottom-left" @close="close"
            :close-on-scroll="false" :prevent-header-overlap="true" :handle-focusable="false" :max-height="504" :content-width="contentWidth" :inherit-width="inheritWidth"
            :container-style="{'min-width': viewSize.mdUp ? '200px' : '0'}">
            <template #default>
                <div class="kool-choiz-label kool-choiz-select-one" @click="toggle">
                    <i v-if="withExpandIcon" class="material-icons kool-choiz-caret" aria-hidden="true" role="img">expand_more</i>
                    <label :id="labelId" class="kool-choiz-label-value text-overflow" v-bind="1 === selected.length && selectedOptions[0] ? selectedOptions[0].attributes : []">
                        <span v-if="0 === selected.length" class="kool-choiz-placeholder" role="textbox" aria-readonly="true">
                            <slot name="placeholder">[ Bitte wählen ]</slot>
                        </span>
                        <slot v-else-if="1 === selected.length" :option="selectedOption" aria-readonly="true" name="one" role="textbox">
                            <div v-if="selectedOption.badgeColor" aria-hidden="true" class="kool-choiz-badge">
                                <div :style="{backgroundColor: selectedOption.badgeColor}" class="kool-choiz-badge-box"></div>
                            </div>
                            <i v-if="selectedOption.icon" :class="[selectedOption.iconClass, {'select-icon-bordered': selectedOption.iconBordered}]" :style="selectedOption.iconStyle" aria-hidden="true"
                                class="material-icons md-18 select-option-spacer" role="img" v-text="selectedOption.icon"/>
                            <div v-if="selectedOption.image" :class="selectedOption.imageClass" :style="{backgroundImage: 'url(' + selectedOption.image + ')'}" aria-hidden="true"
                                class="kool-choiz-image"></div>
                            <div v-if="selectedOption.abbreviation" aria-hidden="true" class="kool-choiz-abbreviation"
                                v-html="getAbbreviationAvatar(selectedOption.abbreviation, 'circle', true, 30, false)">
                            </div>
                            <span>{{selectedOption.selectedLabel || selectedOption.label}}</span>
                        </slot>
                        <slot v-else-if="selected.length === parsedOptions.length" name="all" role="textbox" aria-readonly="true">Alle ausgewählt</slot>
                        <slot v-else-if="selected.length" name="multiple" :count="selected.length" role="textbox" aria-readonly="true">{{selected.length}} ausgewählt</slot>
                    </label>
                </div>
            </template>
            <template #container>
                <div class="kool-choiz-list" :id="listId" role="listbox" :aria-expanded="getPopoverVisible() ? 'true' : 'false'"
                    :aria-hidden="getPopoverVisible() ? 'false' : 'true'">
                    <div ref="topWrapper" class="kool-choiz-top-wrapper">
                        <div class="kool-choiz-item kool-choiz-search" v-if="showSearch">
                            <input type="text" :placeholder="searchPlaceholder" v-model.trim="searchValue" @focus="toggleSearchFocus" @blur="toggleSearchFocus" tabindex="0"
                                ref="searchInput">
                            <i class="material-icons kool-choiz-search-icon" aria-hidden="true" role="img">search</i>
                        </div>
                        <div class="kool-choiz-item kool-choiz-option-all" v-if="multiple" @click="toggleAll" aria-hidden="true" tabindex="0">Sichtbare an/abwählen</div>
                    </div>

                    <div class="kool-choiz-entries" ref="entryContainer">
                        <div v-for="(options, group) in groupedOptions" role="group" :aria-label="group">
                            <div v-if="group" class="kool-choiz-group">{{group}}</div>
                            <div :id="`select_${_uid}_option_${option.index}`" class="kool-choiz-item kool-choiz-option"
                                :class="[{'kool-choiz-option-selected': selected.includes(option.value), 'kool-choiz-item-focused': option.index === focusOption, disabled: option.disabled || isDynamicDeactivated(option)}, option.attributes && option.attributes.class]"
                                v-bind="option.attributes" v-for="option in options" @focus="focusOption = option.index;" @click.prevent="changeOption(option, option.index)" @touchstart.prevent="changeOption(option, option.index)"
                                v-tooltip="tooltip && option.label" role="option" :aria-selected="selected.includes(option.value) ? 'true' : 'false'" :aria-label="option.label"
                                tabindex="0">
                                <div class="kool-choiz-state" aria-hidden="true">
                                    <template v-if="multiple">
                                        <i class="material-icons kool-choiz-state-activated" aria-hidden="true" role="img">check_box</i>
                                        <i class="material-icons kool-choiz-state-deactivated" aria-hidden="true" role="img">check_box_outline_blank</i>
                                    </template>
                                    <template v-else>
                                        <div class="portuna-radio-button" :class="{checked: selected.includes(option.value)}">
                                            <span class="portuna-radio-button-outer"></span>
                                            <span class="portuna-radio-button-inner"></span>
                                        </div>
                                    </template>
                                </div>
                                <div class="kool-choiz-badge" v-if="option.badgeColor" aria-hidden="true">
                                    <div class="kool-choiz-badge-box" :style="{backgroundColor: option.badgeColor}"></div>
                                </div>
                                <div class="kool-choiz-image" v-if="option.image" aria-hidden="true" :class="option.imageClass" :style="{backgroundImage: 'url(' + option.image + ')'}"></div>
                                <div class="kool-choiz-abbreviation" v-if="option.abbreviation" aria-hidden="true"
                                    v-html="getAbbreviationAvatar(option.abbreviation, 'circle', true, 30, false)"></div>
                                <i v-if="option.icon && !option.image" class="material-icons md-18 select-option-spacer" v-text="option.icon"
                                    :style="option.iconStyle" :class="[option.iconClass, {'select-icon-bordered': option.iconBordered}]" aria-hidden="true" role="img"/>
                                <label :id="`select_${_uid}_option_${option.index}_label`" v-text="option.label"
                                    aria-hidden="true"></label>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </app-popover>
    </div>
</template>

<script lang="ts">
    import {getAbbreviationAvatar} from '@/filters/username';
    import {getNumericValue} from '@/library/math';
    import tinycolor from 'tinycolor2';
    import {Component, Prop, Watch} from 'vue-facing-decorator';
    import AbstractFormInput from '../../mixins/AbstractFormInput';

    @Component({
        name: 'FormSelect',
        inheritAttrs: false,
        emits: ['input', 'update:modelValue'],
        methods: {
            getAbbreviationAvatar,

        },
    })
    export class FormSelect extends AbstractFormInput
    {
        readonly $refs: {
            entryContainer: HTMLDivElement,
            popover: AppPopover,
            select: HTMLSelectElement,
            topWrapper: HTMLDivElement,
            searchInput: HTMLInputElement
            container: HTMLDivElement,
        };

        @Prop({type: [Array, Number, String]}) modelValue: FormSelectValue;

        @Prop({type: Boolean}) multiple: boolean;

        @Prop({type: Boolean}) required: boolean;

        @Prop({type: [Array, Object]}) options: [] | {};

        @Prop({
            type: [Number, String],
            default: 0,
            required: false,
        }) tabindex: number | string;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) disabled: boolean;

        @Prop({
            type: [Array, Number, String],
            default: [],
            required: false,
        }) notUnselectable: FormSelectValue;

        @Prop({
            type: Number,
            default: null,
            required: false,
        }) maxSelectAble: Nullable<number>;

        @Prop({
            type: String,
            default: 'value',
            required: false,
        }) valueKey: string;

        @Prop({
            type: String,
            default: 'attributes',
            required: false,
        }) attributesKey: string;

        @Prop({
            type: String,
            default: 'label',
            required: false,
        }) labelKey: string;

        @Prop({
            type: String,
            default: 'long',
            required: false,
            validator: function(value)
            {
                return ['long', 'medium', 'small'].includes(value);
            },
        }) size: string;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) tooltip: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) disableSearch: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) alwaysShowSearch: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) autoComplete: boolean;

        @Prop({
            type: [String, Boolean],
            default: true,
            required: false,
            validator(value: string | boolean): boolean
            {
                return [true, false, 'at-least'].includes(value);
            },
        }) inheritWidth: boolean | 'at-least';

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) contentWidth: boolean;

        @Prop({
            type: String,
            default: 'Suche ...',
            required: false,
        }) searchPlaceholder: string;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) focus: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) noNumberCast: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) selectFirst: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) selectAll: boolean;

        @Prop({
            type: String,
            default: undefined,
            required: false,
        }) selectLabel: string | undefined;

        @Prop({
            type: Boolean,
            default: true,
            required: false,
        }) withExpandIcon: boolean;

        searchValue: string = '';

        selectedModel: Array<number | string> | string | number = [];

        focusOption: number = 0;

        searchFocused: boolean = false;

        disableEmit = false;

        preventEvents = false;

        closedTimeStamp: number = 0;

        displaySearchEntryCount: number = 10;

        get maxEntriesSelected()
        {
            return this.maxSelectAble && Array.isArray(this.selectedModel) && this.selectedModel.length >= this.maxSelectAble;
        }

        /**
         * array of options there are visible
         */
        get shownOptions(): SelectOption[]
        {
            let options: SelectOption[] = [];

            for (let option of this.parsedOptions)
            {
                if (!this.searchValue || this.searchValue && option.label.toLowerCase().includes(this.searchValue.toLowerCase()))
                {
                    options.push(option);
                }
            }

            return options;
        }

        /**
         * all options parsed as array of SelectOption
         */
        get parsedOptions()
        {
            let options: SelectOption[] = [];

            for (let key in this.options)
            {
                if (this.options.hasOwnProperty(key))
                {
                    let value;
                    let attributes;
                    let option        = this.options[key];
                    let label         = option;
                    let abbreviation;
                    let icon;
                    let disabled      = false;
                    let badgeColor;
                    let image;
                    let group         = '';
                    let selectedLabel = '';
                    let iconClass     = '';

                    let iconStyle: Record<string, string> = {};
                    let iconBordered                      = false;
                    let iconBorderColor                   = '';
                    let imageClass                        = '';

                    if (typeof label === 'object' && label)
                    {
                        value           = this.noNumberCast ? label[this.valueKey] : getNumericValue(label[this.valueKey]);
                        label           = label[this.labelKey];
                        abbreviation    = option.abbreviation;
                        icon            = option.icon;
                        disabled        = !!option.disabled;
                        badgeColor      = option.badgeColor;
                        image           = option.image;
                        group           = option.group;
                        iconBorderColor = option.iconBorderColor;
                        iconBordered    = !!option.iconBorderColor;
                        iconClass       = option.iconClass;
                        iconStyle       = option.icon_style || {};
                        selectedLabel   = option.selectedLabel || '';
                        imageClass      = option.imageClass;

                        if (option.iconColor)
                        {
                            iconStyle.color = option.iconColor;

                            if (iconBordered)
                            {
                                let color                 = tinycolor(iconBorderColor);
                                iconStyle.borderColor     = color.setAlpha(0.3).toRgbString();
                                iconStyle.borderStyle     = 'solid';
                                iconStyle.borderWidth     = '1px';
                                iconStyle.borderRadius    = '50%';
                                iconStyle.backgroundColor = color.setAlpha(0.13).toRgbString();
                            }
                        }

                        if (this.attributesKey)
                        {
                            attributes = option[this.attributesKey] || {};

                            if (attributes.style && (attributes.style.includes('border-left-color') || attributes.style.includes('border-bottom')))
                            {
                                attributes.style = 'padding-left:5px;' + attributes.style;
                            }
                        }
                        else
                        {
                            attributes = {};
                        }
                    }
                    else if (typeof label === 'number')
                    {
                        value = label;
                    }
                    else
                    {
                        value = this.noNumberCast ? key : getNumericValue(key);
                    }

                    options.push({
                        value,
                        attributes,
                        label: label.toString(),
                        selectedLabel,
                        abbreviation,
                        icon,
                        iconBordered,
                        iconStyle,
                        iconClass,
                        disabled,
                        badgeColor,
                        image,
                        group,
                        imageClass,
                    });
                }
            }

            return options;
        }

        get selected(): Array<number | string>
        {
            let selected = Array.isArray(this.selectedModel) ? this.selectedModel : [this.selectedModel];
            let index    = 0;

            let added: Array<number | string> = [];

            while (selected.length && index < selected.length)
            {
                let exist = false;
                let value = selected[index];

                if (!added.includes(value))
                {
                    for (let option of this.parsedOptions)
                    {
                        // type sensitive and case-sensitive!!!
                        if (value === option.value)
                        {
                            exist = true;
                            break;
                        }
                    }
                }

                if (!exist)
                {
                    selected.splice(index, 1);
                }
                else
                {
                    added.push(value);
                    index++;
                }

            }

            return selected;
        }

        get selectedOptions(): SelectOption[]
        {
            let selected = this.selected,
                options  = [];

            for (let option of this.parsedOptions)
            {
                if (selected.includes(option.value))
                {
                    options.push(option);
                }
            }

            return options;
        }

        get selectedOption(): Nullable<SelectOption>
        {
            for (let option of this.parsedOptions)
            {
                if (this.selected.includes(option.value))
                {
                    return option;
                }
            }

            return null;
        }

        get groupedOptions()
        {
            let grouped: Record<string, SelectOption[]> = {};
            let count                                   = 0;

            for (let option of this.shownOptions)
            {
                let group = option.group ? option.group : '';

                if (!grouped[group])
                {
                    grouped[group] = [];
                    count++;
                }
            }

            for (let option of this.shownOptions)
            {
                let group = option.group ? option.group : (count > 1 ? 'Sonstige' : '');

                if (!grouped[group])
                {
                    grouped[group] = [];
                }

                grouped[group].push(option);
            }

            let index = 0;

            for (let groupIndex in grouped)
            {
                if (grouped.hasOwnProperty(groupIndex))
                {
                    for (let option of grouped[groupIndex])
                    {
                        option.index = index++;
                    }
                }
            }

            return grouped;
        }

        get showSearch()
        {
            return this.alwaysShowSearch || !this.disableSearch && this.parsedOptions.length >= this.displaySearchEntryCount;
        }

        get labelId()
        {
            return `select_${this._uid}_label`;
        }

        get listId()
        {
            return `select_${this._uid}_list`;
        }

        get metaString()
        {
            let values = [];
            for (let option of this.parsedOptions)
            {
                values.push(option.value);
            }

            return values.join('-');
        }

        getPopoverVisible(): boolean
        {
            return this.$refs.popover?.popoverVisible;
        }

        @Watch('modelValue')
        watchValue(newValue: FormSelectValue)
        {
            this.selectedModel = 'undefined' !== typeof newValue && newValue !== null && (!this.multiple || Array.isArray(newValue)) ? newValue : [];
        }

        @Watch('searchValue')
        watchSearchValue()
        {
            if (this.autoComplete)
            {
                this.focusOption = 0;

                this.$nextTick(() =>
                {
                    this.$refs.popover.container.scrollTop = 0;
                });
            }
            else
            {
                this.focusOption = -1;
            }

            this.$nextTick(this.updateTopWrapperEvent);
        }

        created()
        {
            this.selectedModel = 'undefined' !== typeof this.modelValue && this.modelValue !== null && (!this.multiple || Array.isArray(this.modelValue))
                ? this.modelValue
                : [];

            this.$nextTick().then(() =>
            {
                if (this.focus)
                {
                    this.focusContainer();
                }

                if (0 === this.selectedOptions.length)
                {
                    if (this.selectFirst)
                    {
                        for (let option of this.parsedOptions)
                        {
                            if (option.disabled)
                            {
                                continue;
                            }

                            this.preventEvents = true;
                            this.changeOption(option, option.index);
                            this.preventEvents = false;
                            break;
                        }
                    }
                    else if (this.multiple && this.selectAll)
                    {
                        this.toggleAll();
                    }
                }
            });
        }

        addEvents()
        {
            window.addEventListener('keydown', this.keydownEvent);

            if (this.$refs.topWrapper)
            {
                this.$refs.topWrapper.style.position       = 'absolute';
                this.$refs.entryContainer.style.paddingTop = this.$refs.topWrapper.offsetHeight + 'px';

                this.$nextTick().then(() =>
                {
                    this.$refs.popover.container.addEventListener('scroll', this.updateTopWrapperEvent);
                });
            }
        }

        removeEvents()
        {
            window.removeEventListener('keydown', this.keydownEvent);
        }

        toggle()
        {
            //@ts-ignore:2339 (vue)
            if (this.getPopoverVisible())
            {
                this.close();
            }
            else
            {
                this.open();
            }
        }

        focusContainer()
        {
            this.$refs.container.focus();
        }

        setFocusToSelected()
        {
            let grouped = this.groupedOptions;

            for (let groupIndex in grouped)
            {
                if (grouped.hasOwnProperty(groupIndex))
                {
                    let option: SelectOption;

                    for (option of grouped[groupIndex])
                    {
                        if (this.selected.includes(option.value))
                        {
                            this.focusOption = option.index || 0;
                            return;
                        }
                    }
                }
            }

            this.focusOption = 0;
        }

        get focusedOption(): Nullable<SelectOption>
        {
            let grouped = this.groupedOptions;

            for (let groupIndex in grouped)
            {
                if (grouped.hasOwnProperty(groupIndex))
                {
                    for (let option of grouped[groupIndex])
                    {
                        if (option.index === this.focusOption)
                        {
                            return option;
                        }
                    }
                }
            }

            return null;
        }

        open(event: Nullable<Event> = null)
        {
            event && this.handleSpace(event);

            // prevent opening if either disabled
            // or trying to reopen within 150ms after being closed due to parent-element events
            if (this.disabled || Date.now() - this.closedTimeStamp < 150)
            {
                return;
            }

            if (!this.getPopoverVisible())
            {
                this.setFocusToSelected();

                this.$refs.popover.emitter.once('open', () =>
                {
                    this.addEvents();

                    // scroll to focused element if popover opened and rendered
                    this.scrollToFocused();

                    if (this.$refs.searchInput)
                    {
                        this.$refs.searchInput.focus();
                    }
                    else
                    {
                        this.focusOptionElement();
                    }
                });

                this.$refs.select.blur();
                this.$refs.popover.open();
            }
        }

        focusOptionElement()
        {
            document.getElementById(`select_${this._uid}_option_${this.focusOption}`)?.focus();
        }

        scrollToFocused()
        {
            let container = this.$refs.popover.container;

            if (container)
            {
                let focusElement = container.querySelectorAll('.kool-choiz-item-focused')[0] as undefined | HTMLDivElement;
                let searchHeight = this.showSearch ? this.$refs.topWrapper.offsetHeight : 0;

                if (focusElement)
                {
                    // is not in view
                    if (
                        container.scrollTop + searchHeight > focusElement.offsetTop
                        || focusElement.offsetTop + focusElement.offsetHeight > container.scrollTop + container.offsetHeight
                    )
                    {
                        container.scrollTop = focusElement.offsetTop - searchHeight;
                        this.updateTopWrapperEvent();
                    }

                    return;
                }
            }

            container.scrollTop = 0;
        }

        close()
        {
            this.removeEvents();
            //@ts-ignore:2339 (vue)
            this.$refs.popover?.close();
            this.closedTimeStamp = Date.now();
        }

        //<editor-fold desc="focus handling">
        changeFocus(value: number): void
        {
            let newFocus = this.focusOption + value;
            if (newFocus >= this.shownOptions.length)
            {
                this.focusOption = -1;
            }
            else if (newFocus < -1)
            {
                this.focusOption = this.shownOptions.length - 1;
            }
            else
            {
                this.focusOption = newFocus;
            }

            this.$nextTick(() =>
            {
                this.scrollToFocused();
                this.focusOptionElement();
            });
        }

        next(): void
        {
            this.changeFocus(1);
        }

        previous(): void
        {
            this.changeFocus(-1);
        }

        //</editor-fold>

        keydownEvent(event: KeyboardEvent)
        {
            let key     = event.key;
            let handled = false;

            if (['ArrowDown', 'Down'].includes(key))
            {
                this.searchFocused = false;
                handled            = true;
                this.next();
            }
            else if (['ArrowUp', 'Up'].includes(key))
            {
                this.searchFocused = false;
                handled            = true;
                this.previous();
            }
            else if (['Enter'].includes(key) || [' ', 'Spacebar'].includes(key) && !this.searchFocused)
            {
                handled = true;
                if (-1 === this.focusOption)
                {
                    this.toggleAll();
                }
                else if (this.focusOption < this.shownOptions.length && this.focusedOption)
                {
                    this.changeOption(this.focusedOption, this.focusOption);
                }
            }
            else if (['Escape'].includes(key))
            {
                handled = true;
                this.focusContainer();
                this.$nextTick(() =>
                {
                    this.close();
                });
            }
            else if (['Tab'].includes(key))
            {
                handled = true;
                if (!this.multiple && this.focusOption < this.shownOptions.length && this.focusedOption)
                {
                    this.changeOption(this.focusedOption, this.focusOption);
                }
                this.focusContainer();
                this.$nextTick(() =>
                {
                    this.close();
                });
            }
            else
            {
                this.searchFocused = true;
            }

            if (handled)
            {
                event.preventDefault();
            }
        }

        toggleAll()
        {
            this.disableEmit       = true;
            let selectAll: boolean = false;
            for (let option of this.shownOptions)
            {
                if (!this.selected.includes(option.value))
                {
                    selectAll = true;
                    break;
                }
            }

            for (let option of this.shownOptions)
            {
                if (selectAll && !this.selected.includes(option.value))
                {
                    this.changeOption(option);
                }
                else if (!selectAll && this.selected.includes(option.value))
                {
                    this.changeOption(option);
                }
            }

            this.disableEmit = false;
            this.emitChange();
        }

        isDynamicDeactivated(option: SelectOption)
        {
            const unSelectableOption: boolean = Array.isArray(this.notUnselectable) && this.notUnselectable.includes(option.value)
                || option.value === this.notUnselectable;

            if (unSelectableOption && this.maxEntriesSelected)
            {
                return true;
            }

            if (unSelectableOption)
            {
                return Array.isArray(this.selectedModel) ? this.selectedModel.includes(option.value) : this.selectedModel === option.value;
            }

            return (
                this.maxEntriesSelected
                    ? Array.isArray(this.selectedModel) ? !this.selectedModel.includes(option.value) : this.selectedModel !== option.value
                    : false
            );
        }

        changeOption(option: SelectOption, newIndex: number = -1)
        {
            if (option.disabled)
            {
                return;
            }

            if (this.isDynamicDeactivated(option))
            {
                return;
            }

            let value = option.value;
            if (newIndex >= 0)
            {
                this.focusOption = newIndex;
            }

            if (!this.multiple)
            {
                this.selectedModel = value;

                if (!this.preventEvents)
                {
                    this.focusContainer();
                    this.close();
                }
            }
            else
            {
                if (!Array.isArray(this.selectedModel))
                {
                    this.selectedModel = [];
                }

                if (this.selectedModel.includes(value))
                {
                    this.selectedModel.splice(this.selectedModel.indexOf(value), 1);
                }
                else
                {
                    this.selectedModel.push(value);
                }
            }

            !this.disableEmit && this.emitChange();
        }

        emitChange()
        {
            let newValue = this.multiple ? this.selected : this.selected[0];
            this.$emit('update:modelValue', newValue);
            this.$emit('input', newValue); // legacy for @input usages
            this.validateValue(newValue);
        }

        toggleSearchFocus(event: FocusEvent)
        {
            this.searchFocused = 'focus' === event.type;
        }

        updateTopWrapperEvent()
        {
            let scrollTop = this.$refs.popover.container.scrollTop;

            this.$refs.topWrapper.style.top = scrollTop + 'px';

            if (scrollTop)
            {
                this.$refs.topWrapper.classList.add('floating');
            }
            else
            {
                this.$refs.topWrapper.classList.remove('floating');
            }
        }

        updateValue()
        {
            return this.validateValue(this.multiple ? this.selected : this.selected[0]);
        }

        handleSpace(event: Event)
        {
            !this.searchFocused && event.preventDefault();
        }

        triggerFocus()
        {
            this.open();
        }
    }

    export default FormSelect;
</script>